import { ActivityIndicator } from "react-native";
import { ClientApiError } from "@visioni/a3s-client-api/app";
import { Banner, Button } from "@visioni/a3s-ui/components";
import { Text, View } from "@src/components";

/**
 * Loading and error states for a Patriot tools page body, rendered in place so
 * the page's title and filters stay put.
 *
 * The API's own message is shown verbatim: this is an internal tool, and "not
 * available in this environment" (no Patriot replica in this realm) or "no
 * activation 1234" says more to support than a generic failure would.
 *
 * A 501 is the API saying this environment lacks what the page needs (a
 * Patriot replica, or a connection check) — not a fault, and nothing a retry
 * would change — so it reads as an info notice with no "Try again".
 */
export function QueryNotice({
  isLoading,
  error,
  onRetry,
  loadingLabel = "Loading…",
  errorTitle = "Could not load this",
}: {
  isLoading: boolean;
  error: Error | null;
  onRetry: () => void;
  loadingLabel?: string;
  errorTitle?: string;
}) {
  if (error instanceof ClientApiError && error.status === 501) {
    return <Banner variant="info" title="Not available in this environment" message={error.message} />;
  }
  if (error !== null) {
    return (
      <View gap="md" alignItems="flex-start">
        <Banner variant="warning" title={errorTitle} message={error.message} />
        <Button label="Try again" size="sm" variant="secondary" onPress={onRetry} />
      </View>
    );
  }
  if (isLoading) {
    return (
      <View padding="3xl" alignItems="center" gap="md">
        <ActivityIndicator />
        <Text textStyle="xs/regular" color="textMuted">
          {loadingLabel}
        </Text>
      </View>
    );
  }
  return null;
}
